import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { STORAGE_KEYS, API_BASE_URL } from '../../constants';
import { workspaceApi } from './workspaceApi';
import { notificationListTag, notificationsApi } from './notificationsApi';

export interface FinancialSummary {
  clientRate: number;
  totalRoutesCost: number;
  partnerRoutesCost: number;
  ourRoutesCost: number;
  partnerPaymentAmount: number;
  profit: number;
}

export interface ApplicationFinance {
  id: number;
  internalNumber: string;
  externalNumber: string;
  applicationDate: string;
  status: string;
  client: {
    id: number;
    name: string;
  };
  primaryDocsStatus: string;
  partnerPaymentStatus: string;
  financialSummary: FinancialSummary;
}

export interface FinanceUserBrief {
  id: number;
  username?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: { name: string | null } | null;
}

export interface LogistControlSummary {
  logistId: number | null;
  logistName: string;
  logist?: FinanceUserBrief | null;
  totalRoutes: number;
  routesWithIssues: number;
  criticalCount: number;
  warningCount: number;
  financeIssues: number;
  carrierIssues: number;
  logistIssues: number;
}

export interface LogistRouteWithIssues {
  routeId: number;
  routeNumber: number | string;
  status: string;
  transportType: string;
  plannedLoadingDate?: string | null;
  actualLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualUnloadingDate?: string | null;
  aggregatedStatus: { overallStatus: string };
  controlIssues: {
    critical: { code: string; message: string; responsible: string }[];
    warnings: { code: string; message: string; responsible: string }[];
    info: { code: string; message: string; responsible: string }[];
    totalCount: number;
    hasCritical: boolean;
  };
}

export interface CarrierControlSummary {
  carrierId: number | null;
  carrierName: string;
  totalRoutes: number;
  routesWithIssues: number;
  criticalCount: number;
  lateLoadingCount: number;
  lateUnloadingCount: number;
  missingPhotosCount: number;
  originalsDelayCount: number;
}

export interface CarrierRouteWithIssues {
  routeId: number;
  status: string;
  transportType: string;
  plannedLoadingDate?: string | null;
  actualLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualUnloadingDate?: string | null;
  controlIssues: {
    critical: { code: string; message: string; responsible: string }[];
    warnings: { code: string; message: string; responsible: string }[];
    info: { code: string; message: string; responsible: string }[];
    totalCount: number;
    hasCritical: boolean;
  };
  issuesDescription?: string;
}

export interface ClientControlSummary {
  clientId: number | null;
  clientName: string;
  totalRoutes: number;
  routesWithDebt: number;
  routesWithOverdue: number;
  totalReceivable: number;
  totalRemaining: number;
  overdueAmount: number;
  maxOverdueDays: number;
  routesWithDocsIssues: number;
}

export interface ClientRouteWithIssues {
  routeId: number;
  status: string;
  transportType: string;
  plannedLoadingDate?: string | null;
  actualLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualUnloadingDate?: string | null;
  aggregatedStatus: { overallStatus: string };
  receivable: {
    totalRequired: number;
    totalPaid: number;
    remaining: number;
    overdue: number;
    maxDaysOverdue: number;
  };
  controlIssues: {
    critical: { code: string; message: string; responsible: string }[];
    warnings: { code: string; message: string; responsible: string }[];
    info: { code: string; message: string; responsible: string }[];
    totalCount: number;
    hasCritical: boolean;
  };
  docsIssuesDescription?: string;
}

/** Элемент GET /finance-read/applications-extended */
export interface ApplicationExtendedItem {
  application: Record<string, unknown> & { id: number; internalNumber?: string; externalNumber?: string };
  client: { id: number; name: string } | null;
  responsible: (FinanceUserBrief & { name: string }) | null;
  /** MIN(actualLoadingDate) по неотменённым маршрутам заявки */
  actualLoadingDate?: string | null;
  /** MAX(actualUnloadingDate) по неотменённым маршрутам заявки */
  actualUnloadingDate?: string | null;
  /** План дохода: clientRate или totalCostWithVAT заявки */
  plannedIncome: number;
  income: { total: number; paid: number; remaining: number };
  expense: {
    total: number;
    paid: number;
    remaining: number;
    carrierFact?: number;
    carrierPlan?: number;
    routeExpense?: number;
  };
  profit: number;
  plannedProfit?: number;
  status: 'PAID' | 'PARTIALLY_PAID' | 'NOT_PAID';
  routes: Array<{
    routeId: number;
    carrier: { id: number; name: string | null } | null;
    /** План расхода: totalAmount маршрута */
    plannedExpense: number;
    total: number;
    paid: number;
    remaining: number;
    status: 'PAID' | 'PARTIALLY_PAID' | 'NOT_PAID';
  }>;
}

/** Элемент GET /finance-read/obligations */
export interface FinanceObligationReadItem {
  id: number;
  type: 'INCOMING' | 'OUTGOING';
  stage: string;
  counterpartyName: string;
  applicationId: number | null;
  routeId: number | null;
  applicationInternalNumber: string | null;
  /** internalNumber / externalNumber заявки */
  clientRequestNumber: string | null;
  /** Номер договора-заявки с перевозчиком (маршрут) */
  carrierContractRequestNumber: string | null;
  amount: number;
  paidAmount: number;
  remainingAmount: number;
  dueDate: string | null;
  daysLeft: number | null;
  status: string;
}

export interface RouteFinance {
  id: number;
  transportType: string;
  status: string;
  totalAmount: number | null;
  income: number | null;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualLoadingDate?: string | null;
  actualUnloadingDate?: string | null;
  responsibleLogistId?: number | null;
  responsibleLogist?: FinanceUserBrief | null;
  prepaymentPercent: number | null;
  prepaymentAmount: number | null;
  remainingAmount: number | null;
  prepaymentDelayDays: number | null;
  prepaymentStartDate: string | null;
  mainPaymentDelayDays: number | null;
  mainPaymentStartDate: string | null;
  executor: string;
  isCompanyRoute?: boolean;
  loadingAddress: string | null;
  unloadingAddress: string | null;
  carrier: any;
  shipper: any;
  consignee: any;
  driver: any;
  vehicle: any;
  loadingDocuments: any[];
  transportDocuments: any[];
  payments: any[];
            application?: {
            id: number;
            internalNumber: string;
            externalNumber: string;
            client: {
              id: number;
              name: string;
            };
          };
}

export interface Payment {
  id: number;
  applicationId: number | null;
  routeId: number | null;
  obligationId?: number | null;
  /** PREPAYMENT / FINAL_PAYMENT / APPLICATION / PARTNER_RECEIPT и др. */
  paymentType: string;
  amountToPay: number;
  amountPaid: number;
  paymentDate: string | null;
  status: 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  daysToPayment: number | null;
  createdAt: string;
  updatedAt: string;
  application?: {
    id: number;
    internalNumber: string;
    externalNumber: string;
    client: {
      id: number;
      name: string;
    };
  };
  route?: {
    id: number;
    transportType: string;
    carrier: {
      id: number;
      name: string;
    };
    isCompanyRoute?: boolean;
  };
  invoice?: { id: number; number: string } | null;
  obligation?: { id: number; direction?: string; stage?: string } | null;
  allocations?: Array<{
    obligation?: { id: number; stage?: string; direction?: string } | null;
  }>;
}

export interface PaymentsListResponse {
  payments: Payment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CreatePaymentRequest {
  /** Если указаны только applicationId + amount (+ дата/комментарий) — сервер распределяет по INCOME/CLIENT */
  applicationId?: number;
  amount: number;
  paymentDate?: string;
  comment?: string;
  routeId?: number;
  obligationId?: number;
  paymentType?: 'PREPAYMENT' | 'FINAL_PAYMENT';
}

/** Ответ POST /finance/payments: один Payment или обёртка с распределением по заявке */
export type CreatePaymentResult =
  | Payment
  | { payment: Payment; allocations: Array<{ obligationId: number; amount: number }> };

export function getCreatedPaymentId(result: CreatePaymentResult | unknown): number | undefined {
  if (result == null || typeof result !== 'object') return undefined;
  const r = result as Record<string, unknown>;
  if (typeof r.id === 'number') return r.id;
  const p = r.payment as Record<string, unknown> | undefined;
  if (p && typeof p.id === 'number') return p.id;
  const payments = r.payments;
  if (Array.isArray(payments) && payments.length > 0) {
    const first = payments[0] as { id?: unknown };
    if (typeof first?.id === 'number') return first.id;
  }
  return undefined;
}

export interface UpdatePaymentRequest {
  amountPaid: number;
  paymentDate?: string;
}

export interface AvailableRouteForPayment {
  id: number;
  transportType: string;
  status: string;
  totalAmount: number | null;
  prepaymentAmount: number | null;
  prepaymentPercent?: number | null;
  remainingAmount: number | null;
  loadingAddress: string | null;
  unloadingAddress: string | null;
  carrier: any;
  shipper: any;
  consignee: any;
  hasLoadingDocs: boolean;
  hasTransportDocs: boolean;
  isCompanyRoute?: boolean;
  applications: {
    id: number;
    internalNumber: string;
    externalNumber: string;
    client: any;
  }[];
  existingPayments: number;
  // Дополнительные агрегаты для UX
  isPrepaymentClosed?: boolean;
  isFinalClosed?: boolean;
  prepaymentRequired?: number | null;
  finalRequired?: number | null;
  prepaymentDebt?: number | null;
  finalDebt?: number | null;
}

export interface RoutePaymentCounters {
  routeId: number;
  prepaymentCounter: {
    active: boolean;
    startDate: string | null;
    delayDays: number | null;
    daysRemaining: number | null;
  };
  mainPaymentCounter: {
    active: boolean;
    startDate: string | null;
    delayDays: number | null;
    daysRemaining: number | null;
  };
  paymentStatus: string;
}

export interface RouteObligationForPayment {
  id: number;
  direction: string;
  stage: string;
  amountWithVAT: number;
  remainingAmount?: number;
  dueDate: string | null;
  routeId?: number | null;
  applicationId?: number | null;
}

export interface RoutesFinanceListResponse {
  routes: RouteFinance[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ApplicationFinanceDetail {
  application: {
    id: number;
    internalNumber: string;
    externalNumber: string;
    status: string;
    clientRate: number | null;
    primaryDocsStatus: string;
    primaryDocsSignedDate: string | null;
    partnerPaymentStatus: string;
    partnerPaymentAmount: number | null;
    partnerPaymentDate: string | null;
    client: any;
  };
  financialSummary: FinancialSummary;
  routes: RouteFinance[];
}

export interface FinanceStatistics {
  totalApplications: number;
  completedApplications: number;
  signedDocsApplications: number;
  receivedPaymentsApplications: number;
  financialSummary: {
    totalClientRate: number;
    totalRoutesCost: number;
    totalPartnerRoutesCost: number;
    totalOurRoutesCost: number;
    totalPartnerPaymentAmount: number;
    totalProfit: number;
  };
}

export interface ApplicationsFinanceListResponse {
  applications: ApplicationFinance[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Новые типы для документов финансового модуля
export interface LoadingDocumentDTO {
  id: number;
  routeId: number;
  loadingDate: string;
  photos: string[];
  invoiceDocument: string | null;
  uploadedAt: string;
  uploadedBy: number;
}

export interface TransportDocumentDTO {
  id: number;
  routeId: number;
  deliveryType: 'MAIL' | 'EDO' | 'PERSONAL';
  trackingNumber?: string | null;
  receivedDate?: string | null;
  documents: string[];
  uploadedAt: string;
  uploadedBy: number;
}

export interface RouteExpense {
  id: number;
  routeId: number;
  type: string;
  status?: 'DRAFT' | 'CONFIRMED' | 'REJECTED' | string;
  createdByRole?: 'LOGIST' | 'ACCOUNTANT' | 'ADMIN' | string;
  description?: string | null;
  amountWithVAT: number;
  vatRate: number;
  amountWithoutVAT: number;
  expenseDate: string;
  currency?: string;
  obligationId?: number | null;
  invoiceId?: number | null;
  createdBy: number;
  confirmedById?: number | null;
  confirmedAt?: string | null;
  createdAt: string;
  allocationMode?: 'COMMON' | 'DIRECT' | 'UNALLOCATED';
  directApplicationId?: number | null;
  allocations?: Array<{ applicationId: number; share: number; allocatedAmount: number }>;
  invoice?: { id: number; number: string; invoiceDate?: string; amountWithVAT?: number } | null;
}

export interface SetRouteExpenseAllocationRequest {
  allocationMode: 'COMMON' | 'DIRECT';
  directApplicationId?: number;
}

export interface RouteExpenseAllocationCandidate {
  applicationId: number;
  number?: string;
  status?: string;
  rate?: number | null;
}

export interface RouteExpensesQueryResult {
  expenses: RouteExpense[];
  allocationCandidates: RouteExpenseAllocationCandidate[];
}

export interface CreateRouteExpenseRequest {
  type: string;
  description?: string;
  amountWithVAT: number;
  vatRate?: number;
  expenseDate?: string;
  invoiceId?: number;
  allocationMode?: 'COMMON' | 'DIRECT' | 'UNALLOCATED';
  directApplicationId?: number | null;
}

export interface UpdateRouteExpenseRequest {
  type?: string;
  description?: string;
  amountWithVAT?: number;
  vatRate?: number;
  expenseDate?: string;
}

export interface RouteFinanceSummary {
  income: number;
  carrierExpense: number;
  additionalExpense: number;
  totalExpense: number;
  profit: number;
}

export interface InvoiceListItem {
  id: number;
  number: string;
  invoiceDate: string;
  amountWithVAT: number;
  currency?: string;
  direction: string;
  counterpartyType: string;
  applicationId?: number | null;
  routeId?: number | null;
  obligationId?: number | null;
  fileDocumentId?: number | null;
  sentToClientAt?: string | null;
  fileDownloadedAt?: string | null;
  fileDownloadCount?: number;
  status?: string;
  isAdjustment?: boolean;
  parentInvoiceId?: number | null;
  reason?: string | null;
  parentInvoice?: { id: number; number: string } | null;
  application?: { id: number; internalNumber?: string; client?: { id: number; name: string } } | null;
  route?: { id: number; carrier?: { id: number; name: string } } | null;
  obligation?: { id: number } | null;
  payments?: any[];
}

export interface InvoicesListResponse {
  invoices: InvoiceListItem[];
  pagination: { page: number; limit: number; total: number };
}

export interface ReceivableListItem {
  id: number;
  applicationId?: number | null;
  routeId?: number | null;
  dueDate: string | null;
  status: string;
  amount: number;
  paid: number;
  remaining: number;
  totalAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  obligationStatus?: string;
  application?: { id: number; internalNumber?: string; client?: { id: number; name: string } } | null;
  route?: { id: number } | null;
  invoices?: any[];
  payments?: any[];
  paymentAllocations?: { id: number; amount: number; paymentId: number }[];
}

export interface ReceivablesListResponse {
  receivables: ReceivableListItem[];
  pagination: { page: number; limit: number; total: number };
}

export interface PayableListItem {
  id: number;
  routeId?: number | null;
  dueDate: string | null;
  status: string;
  amount: number;
  paid: number;
  remaining: number;
  route?: { id: number; carrier?: { id: number; name: string } } | null;
  invoices?: any[];
  payments?: any[];
}

export interface PayablesListResponse {
  payables: PayableListItem[];
  pagination: { page: number; limit: number; total: number };
}

export interface FinanceDashboardSummary {
  revenue: number;
  expenses: number;
  profit: number;
  receivables: number;
  payables: number;
  overdueReceivables: number;
  overduePayables: number;
}

export const financeApi = createApi({
  reducerPath: 'financeApi',
  baseQuery: fetchBaseQuery({ 
    baseUrl: API_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: ['Finance', 'ApplicationFinance', 'Payments', 'Route', 'RouteExpenses', 'Workspace'],
  endpoints: (builder) => ({
    // Получить список заявок с финансовой информацией
    getApplicationsFinanceList: builder.query<ApplicationsFinanceListResponse, {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
    }>({
      query: (params) => ({
        url: '/finance/applications',
        params
      }),
      transformResponse: (response: { success: boolean; data: { applications: any[]; pagination: any } }) => {
        // Преобразуем данные в нужный формат
        const applications = response.data.applications.map(app => ({
          id: app.id,
          internalNumber: app.internalNumber,
          externalNumber: app.externalNumber,
          applicationDate: app.applicationDate,
          status: app.status,
          client: app.client,
          primaryDocsStatus: app.primaryDocsStatus,
          partnerPaymentStatus: app.partnerPaymentStatus,
          // Поля для своих заявок
          totalCostWithVAT: app.totalCostWithVAT || app.total_cost_with_vat || 0,
          prepaymentPercent: app.prepaymentPercent ?? app.prepayment_percent ?? 0,
          mainPaymentPercent: app.mainPaymentPercent ?? app.main_payment_percent,
          prepaymentDelayDays: app.prepaymentDelayDays ?? app.prepayment_delay_days,
          mainPaymentDelayDays: app.mainPaymentDelayDays ?? app.main_payment_delay_days,
          prepaymentStartDate: app.prepaymentStartDate ?? app.prepayment_start_date,
          mainPaymentStartDate: app.mainPaymentStartDate ?? app.main_payment_start_date,
          prepaymentStatus: app.prepaymentStatus ?? app.prepayment_status ?? 'NOT_PAID',
          mainPaymentStatus: app.mainPaymentStatus ?? app.main_payment_status ?? 'NOT_PAID',
          prepaymentReceivedAmount: app.prepaymentReceivedAmount ?? app.prepayment_received_amount,
          prepaymentReceivedDate: app.prepaymentReceivedDate ?? app.prepayment_received_date,
          mainPaymentReceivedAmount: app.mainPaymentReceivedAmount ?? app.main_payment_received_amount,
          mainPaymentReceivedDate: app.mainPaymentReceivedDate ?? app.main_payment_received_date,
          financialSummary: {
            clientRate: app.financialSummary?.clientRate || 0,
            totalRoutesCost: app.financialSummary?.totalRoutesCost || 0,
            partnerRoutesCost: app.financialSummary?.partnerRoutesCost || 0,
            ourRoutesCost: app.financialSummary?.ourRoutesCost || 0,
            partnerPaymentAmount: app.financialSummary?.partnerPaymentAmount || 0,
            partnerReceived: app.financialSummary?.partnerReceived || 0,
            expectedProfit: app.financialSummary?.expectedProfit || 0,
            realizedProfit: app.financialSummary?.realizedProfit || 0,
            // legacy field for compatibility
            profit: app.financialSummary?.expectedProfit || app.financialSummary?.profit || 0
          }
        }));
        
        return {
          applications,
          pagination: response.data.pagination
        };
      },
      providesTags: ['Finance']
    }),

    // Получить финансовую сводку по заявке
    getApplicationFinance: builder.query<ApplicationFinanceDetail, number>({
      query: (applicationId) => `/finance/applications/${applicationId}`,
      providesTags: (result, error, applicationId) => [
        { type: 'ApplicationFinance', id: applicationId }
      ]
    }),

    // Обновить статус первичных документов
    updatePrimaryDocsStatus: builder.mutation<any, {
      applicationId: number;
      status: 'NOT_SIGNED' | 'SIGNED';
      signedDate?: string;
    }>({
      query: ({ applicationId, ...body }) => ({
        url: `/finance/applications/${applicationId}/primary-docs`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        'Finance',
        { type: 'ApplicationFinance', id: applicationId }
      ]
    }),

    // Обновить статус платежа от партнера
    updatePartnerPaymentStatus: builder.mutation<any, {
      applicationId: number;
      status: 'NOT_RECEIVED' | 'RECEIVED';
      amount?: number;
      paymentDate?: string;
    }>({
      query: ({ applicationId, ...body }) => ({
        url: `/finance/applications/${applicationId}/partner-payment`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        'Finance',
        { type: 'ApplicationFinance', id: applicationId }
      ]
    }),

    // Установить сумму к получению от партнёра (без смены статуса)
    setPartnerReceivable: builder.mutation<any, { applicationId: number; amount: number }>({
      query: ({ applicationId, amount }) => ({
        url: `/finance/applications/${applicationId}/partner-receivable`,
        method: 'PATCH',
        body: { amount }
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        'Finance',
        { type: 'ApplicationFinance', id: applicationId }
      ]
    }),

    // Read-model: obligation-based маршруты (без фронтовых расчётов)
    getRoutesReadModel: builder.query<any, {
      page?: number;
      limit?: number;
      status?: string;
      transportType?: string;
      search?: string;
      application?: string;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
    }>({
      query: (params) => ({
        url: '/finance-read/routes',
        params
      }),
      transformResponse: (response: { success: boolean; data: { routes: any[]; pagination: any } }) => ({
        routes: response.data?.routes || [],
        pagination: response.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
      }),
      providesTags: ['Finance']
    }),

    // Расширенный read-model: полные маршруты + obligation-based aggregatedStatus, payable, receivable
    getRoutesExtended: builder.query<{
      routes: any[];
      pagination: any;
      tabCounts?: {
        all: number;
        awaitLoading: number;
        inTransit: number;
        awaitPayment: number;
        completed: number;
        cancelled: number;
        issues: number;
        readyForCompletion: number;
      };
      statistics?: {
        total: number;
        auto: number;
        railway: number;
        aviation: number;
        inTransit: number;
        delivered: number;
      };
      viewMeta?: {
        awaitPayment: {
          overdueCount: number;
          partialCount: number;
          notPaidCount: number;
        };
      };
      view?: string;
    }, {
      page?: number;
      limit?: number;
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      /** all | own | carriers — трёхпозиционный фильтр «исполнения»; приоритет над companyOnly */
      executionScope?: 'all' | 'own' | 'carriers' | string;
      responsibleLogistId?: number;
      carrierId?: number;
      routeId?: number;
      scope?: 'active' | 'archive';
      activeOnly?: boolean;
      clientId?: number;
      driverId?: number;
      vehicleId?: number;
      view?: 'await_loading' | 'in_transit' | 'await_payment' | 'completed' | 'cancelled' | 'issues' | 'ready_for_completion';
      paymentStatus?: 'all' | 'ok' | 'partial' | 'open' | 'overdue' | 'await_payment';
      includePresetCounts?: boolean;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page != null) searchParams.append('page', String(params.page));
        if (params.limit != null) searchParams.append('limit', String(params.limit));
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.executionScope && params.executionScope !== 'all') {
          searchParams.append('executionScope', String(params.executionScope));
        }
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        if (params.routeId != null) searchParams.append('routeId', String(params.routeId));
        if (params.scope) searchParams.append('scope', params.scope);
        if (params.activeOnly != null) searchParams.append('activeOnly', String(params.activeOnly));
        if (params.clientId != null) searchParams.append('clientId', String(params.clientId));
        if (params.driverId != null) searchParams.append('driverId', String(params.driverId));
        if (params.vehicleId != null) searchParams.append('vehicleId', String(params.vehicleId));
        if (params.view) searchParams.append('view', params.view);
        if (params.paymentStatus) searchParams.append('paymentStatus', params.paymentStatus);
        if (params.includePresetCounts != null) {
          searchParams.append('includePresetCounts', String(params.includePresetCounts));
        }
        return {
          url: `/finance-read/routes-extended?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: {
        success: boolean;
        data: {
          routes: any[];
          pagination: any;
          tabCounts?: {
            all: number;
            awaitLoading: number;
            inTransit: number;
            awaitPayment: number;
            completed: number;
            cancelled: number;
            issues: number;
            readyForCompletion: number;
          };
          statistics?: {
            total: number;
            auto: number;
            railway: number;
            aviation: number;
            inTransit: number;
            delivered: number;
          };
          viewMeta?: {
            awaitPayment: {
              overdueCount: number;
              partialCount: number;
              notPaidCount: number;
            };
          };
          view?: string;
          paymentStatus?: string;
        };
      }) => ({
        routes: response.data?.routes || [],
        pagination: response.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 },
        tabCounts: response.data?.tabCounts,
        statistics: response.data?.statistics,
        viewMeta: response.data?.viewMeta,
        view: response.data?.view,
      }),
      providesTags: ['Finance']
    }),

    getApplicationsExtended: builder.query<
      { applicationsExtended: ApplicationExtendedItem[]; pagination: { page: number; limit: number; total: number; pages: number } },
      {
        page?: number;
        limit?: number;
        search?: string;
        clientId?: number;
        status?: string;
        applicationId?: number;
        responsibleLogistId?: number;
        dateFrom?: string;
        dateTo?: string;
        direction?: string;
      }
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page != null) searchParams.append('page', String(params.page));
        if (params.limit != null) searchParams.append('limit', String(params.limit));
        if (params.search) searchParams.append('search', params.search);
        if (params.clientId != null) searchParams.append('clientId', String(params.clientId));
        if (params.status) searchParams.append('status', params.status);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.direction) searchParams.append('direction', params.direction);
        return {
          url: `/finance-read/applications-extended?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: {
        success: boolean;
        data: { applicationsExtended: ApplicationExtendedItem[]; pagination: { page: number; limit: number; total: number; pages: number } };
      }) => ({
        applicationsExtended: response.data?.applicationsExtended || [],
        pagination: response.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
      }),
      providesTags: ['Finance']
    }),

    getObligationsRead: builder.query<
      { obligations: FinanceObligationReadItem[]; pagination: { page: number; limit: number; total: number; pages: number } },
      { page?: number; limit?: number; type?: 'INCOMING' | 'OUTGOING'; search?: string; includePaid?: boolean }
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page != null) searchParams.append('page', String(params.page));
        if (params.limit != null) searchParams.append('limit', String(params.limit));
        if (params.type) searchParams.append('type', params.type);
        if (params.search) searchParams.append('search', params.search);
        if (params.includePaid) searchParams.append('includePaid', 'true');
        return {
          url: `/finance-read/obligations?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: {
        success: boolean;
        data: { obligations: FinanceObligationReadItem[]; pagination: { page: number; limit: number; total: number; pages: number } };
      }) => ({
        obligations: response.data?.obligations || [],
        pagination: response.data?.pagination || { page: 1, limit: 100, total: 0, pages: 0 }
      }),
      providesTags: ['Finance']
    }),

    // Сводка контроля по логистам
    getLogistsControlSummary: builder.query<{ logists: LogistControlSummary[] }, {
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        return {
          url: `/finance-read/logists-control?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { logists: any[] } }) => ({
        logists: response.data?.logists || []
      }),
      providesTags: ['Finance']
    }),

    // Маршруты с нарушениями по логисту (drill-down)
    getLogistRoutesWithIssues: builder.query<{ routes: LogistRouteWithIssues[] }, {
      logistId: number | 'unassigned';
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      carrierId?: number;
    }>({
      query: ({ logistId, ...params }) => {
        const logistIdStr = logistId === 'unassigned' ? 'unassigned' : String(logistId);
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        return {
          url: `/finance-read/logists/${logistIdStr}/routes?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { routes: any[] } }) => ({
        routes: response.data?.routes || []
      }),
      providesTags: ['Finance']
    }),

    // Сводка контроля по перевозчикам
    getCarriersControlSummary: builder.query<{ carriers: CarrierControlSummary[] }, {
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        return {
          url: `/finance-read/carriers-control?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { carriers: any[] } }) => ({
        carriers: response.data?.carriers || []
      }),
      providesTags: ['Finance']
    }),

    // Маршруты с нарушениями перевозчика (drill-down)
    getCarrierRoutesWithIssues: builder.query<{ routes: CarrierRouteWithIssues[] }, {
      carrierId: number;
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      responsibleLogistId?: number;
    }>({
      query: ({ carrierId, ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        return {
          url: `/finance-read/carriers/${carrierId}/routes?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { routes: any[] } }) => ({
        routes: response.data?.routes || []
      }),
      providesTags: ['Finance']
    }),

    // Сводка контроля по клиентам
    getClientsControlSummary: builder.query<{ clients: ClientControlSummary[] }, {
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
      clientId?: number;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        if (params.clientId != null) searchParams.append('clientId', String(params.clientId));
        return {
          url: `/finance-read/clients-control?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { clients: any[] } }) => ({
        clients: response.data?.clients || []
      }),
      providesTags: ['Finance']
    }),

    // Маршруты с долгом/просрочкой/проблемами клиента (drill-down)
    getClientRoutesWithIssues: builder.query<{ routes: ClientRouteWithIssues[] }, {
      clientId: number;
      status?: string;
      transportType?: string;
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      applicationId?: number;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
    }>({
      query: ({ clientId, ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.append('status', params.status);
        if (params.transportType) searchParams.append('transportType', params.transportType);
        if (params.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params.search) searchParams.append('search', params.search);
        if (params.applicationId != null) searchParams.append('applicationId', String(params.applicationId));
        if (params.companyOnly != null) searchParams.append('companyOnly', String(params.companyOnly));
        if (params.responsibleLogistId != null) searchParams.append('responsibleLogistId', String(params.responsibleLogistId));
        if (params.carrierId != null) searchParams.append('carrierId', String(params.carrierId));
        return {
          url: `/finance-read/clients/${clientId}/routes?${searchParams.toString()}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { success: boolean; data: { routes: any[] } }) => ({
        routes: response.data?.routes || []
      }),
      providesTags: ['Finance']
    }),

    // Получить список маршрутов с финансовой информацией
    getRoutesFinanceList: builder.query<RoutesFinanceListResponse, {
      page?: number;
      limit?: number;
      status?: string;
      transportType?: string;
      search?: string;
      application?: string;
      companyOnly?: boolean;
      responsibleLogistId?: number;
      carrierId?: number;
    }>({
      query: (params) => ({
        url: '/finance/routes',
        params
      }),
      transformResponse: (response: { success: boolean; data: { routes: any[]; pagination: any } }) => {
        // Normalize backend response to expected frontend fields
        const rawRoutes = response.data.routes || [];
        const routes = rawRoutes.map((r: any) => {
          const totalAmount = r.totalAmount ?? r.total_amount ?? r.cost ?? r.total ?? null;
          const income = r.income ?? r.incomeAmount ?? r.clientRate ?? null;
          const executor = r.executor ?? r.payer ?? (r.isCompanyRoute || r.is_company_route ? 'COMPANY' : undefined) ?? r.executor;
          const isCompanyRoute = (r.isCompanyRoute ?? r.is_company_route) ?? (executor === 'COMPANY') ?? false;

          // Normalize transport type field (backend may return transport_type)
          const transportType = r.transportType ?? r.transport_type ?? r.type ?? undefined;

          return {
            ...r,
            transportType,
            executor,
            totalAmount: totalAmount !== null ? Number(totalAmount) : null,
            income: income !== null ? Number(income) : null,
            isCompanyRoute,
          } as any;
        });

        // Debug normalized routes for troubleshooting
        console.debug('🧾 Finance API - normalized routes:', routes);
        return {
          routes,
          pagination: response.data.pagination
        };
      },
      providesTags: ['Finance']
    }),

    // Получить финансовую статистику
    getFinanceStatistics: builder.query<FinanceStatistics, {
      startDate?: string;
      endDate?: string;
    }>({
      query: (params) => ({
        url: '/finance/statistics',
        params
      }),
      transformResponse: (response: { success: boolean; data: any }) => {
        const d = response.data || {};
        return {
          totalApplications: Number(d.totalApplications || 0),
          completedApplications: Number(d.completedApplications || 0),
          signedDocsApplications: Number(d.signedDocsApplications || 0),
          receivedPaymentsApplications: Number(d.receivedPaymentsApplications || 0),
          financialSummary: {
            totalClientRate: Number(d.financialSummary?.totalClientRate || 0),
            totalRoutesCost: Number(d.financialSummary?.totalRoutesCost || 0),
            totalPartnerRoutesCost: Number(d.financialSummary?.totalPartnerRoutesCost || 0),
            totalOurRoutesCost: Number(d.financialSummary?.totalOurRoutesCost || 0),
            totalPartnerPaymentAmount: Number(d.financialSummary?.totalPartnerPaymentAmount || 0),
            totalProfit: Number(d.financialSummary?.totalProfit || 0),
          }
        } as FinanceStatistics;
      },
      providesTags: ['Finance']
    }),

    // Получить доступные маршруты для создания платежей
    getAvailableRoutesForPayments: builder.query<AvailableRouteForPayment[], void>({
      query: () => '/finance/available-routes-for-payments',
      transformResponse: (response: { success: boolean; data: AvailableRouteForPayment[] }) => {
        return response.data;
      },
      providesTags: ['Finance']
    }),

    // Получить информацию о счетчиках платежей для маршрута
    getRoutePaymentCounters: builder.query<RoutePaymentCounters, number>({
      query: (routeId) => `/finance/routes/${routeId}/counters`,
      transformResponse: (response: { success: boolean; data: RoutePaymentCounters }) => {
        return response.data;
      },
      providesTags: ['Finance']
    }),

    // Получить обязательства маршрута для выбора при создании платежа (obligation-based)
    getRouteObligationsForPayment: builder.query<RouteObligationForPayment[], number>({
      query: (routeId) => `/finance/routes/${routeId}/counters`,
      transformResponse: (response: { success: boolean; data: any }) => {
        const obligations = response.data?.obligations || [];
        return obligations.map((o: any) => {
          const paidP = (o.payments || []).reduce((s: number, p: any) => s + Number(p.amountPaid || p.amountToPay || 0), 0);
          const paidA = (o.paymentAllocations || []).reduce((s: number, a: any) => s + Number(a.amount || 0), 0);
          const totalPaid = paidP + paidA;
          const amountWithVAT = Number(o.amountWithVAT || 0);
          const applied = Number(o.appliedAmount || 0);
          const remainingAmount = Math.max(0, amountWithVAT - totalPaid - applied);
          return {
            id: o.id,
            direction: o.direction,
            stage: o.stage,
            amountWithVAT,
            remainingAmount,
            dueDate: o.dueDate,
            routeId: o.routeId,
            applicationId: o.applicationId
          };
        }).filter((o: RouteObligationForPayment) => (o.remainingAmount ?? 0) > 0);
      },
      providesTags: ['Finance']
    }),

    // Получить список платежей (реестр; obligationId — по обязательству, сортировка paymentDate ASC на бэкенде)
    getPaymentsList: builder.query<PaymentsListResponse, {
      page?: number;
      limit?: number;
      status?: string;
      paymentType?: string;
      applicationId?: number;
      routeId?: number;
      obligationId?: number;
      companyOnly?: boolean;
      search?: string;
    }>({
      query: (params) => ({
        url: '/finance/payments',
        method: 'GET',
        params
      }),
      transformResponse: (response: { success: boolean; data?: any; pagination?: any }) => {
        const data = response?.data;
        const pagination = (data && typeof data === 'object' && 'pagination' in data)
          ? data.pagination
          : response.pagination;
        const payments = (data && Array.isArray(data))
          ? data
          : (data && typeof data === 'object' && 'payments' in data)
            ? data.payments
            : [];
        return {
          payments: payments || [],
          pagination: pagination || { page: 1, limit: 20, total: 0, pages: 0 }
        };
      },
      providesTags: (result, error, arg) =>
        arg?.obligationId
          ? [{ type: 'Payments', id: `obligation-${arg.obligationId}` }, 'Payments']
          : ['Payments']
    }),

    // Список счетов
    getInvoicesList: builder.query<InvoicesListResponse, {
      page?: number;
      limit?: number;
      direction?: string;
      counterpartyType?: string;
      applicationId?: number;
      routeId?: number;
      search?: string;
      /** true — включая CANCELLED */
      includeCancelled?: boolean;
    }>({
      query: (params) => {
        const { includeCancelled, ...rest } = params;
        return {
          url: '/finance/invoices',
          method: 'GET',
          params: {
            ...rest,
            ...(includeCancelled ? { includeCancelled: 'true' } : {})
          }
        };
      },
      transformResponse: (response: { success: boolean; data: InvoicesListResponse }) => response?.data ?? { invoices: [], pagination: { page: 1, limit: 20, total: 0 } },
      providesTags: ['Finance']
    }),

    // Отправить счёт клиенту (body: sentToClientAt опционально — иначе сервер ставит now)
    sendInvoiceToClient: builder.mutation<
      { status?: string; sentToClientAt: string; obligationId?: number; idempotent?: boolean },
      { invoiceId: number; sentToClientAt?: string; comment?: string }
    >({
      query: ({ invoiceId, sentToClientAt, comment }) => ({
        url: `/finance/invoices/${invoiceId}/send`,
        method: 'POST',
        body: {
          ...(sentToClientAt ? { sentToClientAt } : {}),
          ...(comment != null && comment.trim() !== '' ? { comment: comment.trim() } : {})
        }
      }),
      transformResponse: (response: { success: boolean; data: { status?: string; sentToClientAt: string; obligationId?: number; idempotent?: boolean } }) =>
        response?.data ?? { sentToClientAt: '' },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(workspaceApi.util.invalidateTags(['ApplicationControlTasks']));
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* empty */
        }
      },
      invalidatesTags: ['Finance', 'Workspace']
    }),

    updateInvoice: builder.mutation<
      InvoiceListItem,
      {
        id: number;
        number?: string;
        invoiceDate?: string;
        amountWithVAT?: number;
        file?: File | null;
      }
    >({
      query: ({ id, number, invoiceDate, amountWithVAT, file }) => {
        if (file) {
          const fd = new FormData();
          if (number != null) fd.append('number', number);
          if (invoiceDate) fd.append('invoiceDate', invoiceDate);
          if (amountWithVAT != null) fd.append('amountWithVAT', String(amountWithVAT));
          fd.append('file', file);
          return { url: `/finance/invoices/${id}`, method: 'PATCH', body: fd };
        }
        return {
          url: `/finance/invoices/${id}`,
          method: 'PATCH',
          body: {
            ...(number != null ? { number } : {}),
            ...(invoiceDate ? { invoiceDate } : {}),
            ...(amountWithVAT != null ? { amountWithVAT } : {})
          }
        };
      },
      transformResponse: (response: { success: boolean; data: InvoiceListItem }) => response?.data as InvoiceListItem,
      invalidatesTags: ['Finance', 'Workspace']
    }),

    cancelInvoice: builder.mutation<{ id: number; status: string }, number>({
      query: (id) => ({ url: `/finance/invoices/${id}`, method: 'DELETE' }),
      transformResponse: (response: { success: boolean; data: { id: number; status: string } }) =>
        response?.data ?? { id: 0, status: 'CANCELLED' },
      invalidatesTags: ['Finance', 'Workspace']
    }),

    adjustClientInvoice: builder.mutation<
      {
        invoice: InvoiceListItem;
        obligation: Record<string, unknown>;
        totals: { netAmountWithVAT: number; obligationsCount: number };
      },
      { invoiceId: number; amountWithVAT: number; reason?: string }
    >({
      query: ({ invoiceId, amountWithVAT, reason }) => ({
        url: `/finance/invoices/${invoiceId}/adjust`,
        method: 'POST',
        body: {
          amountWithVAT,
          ...(reason != null && String(reason).trim() !== '' ? { reason: String(reason).trim() } : {})
        }
      }),
      transformResponse: (response: {
        success: boolean;
        data: {
          invoice: InvoiceListItem;
          obligation: Record<string, unknown>;
          totals: { netAmountWithVAT: number; obligationsCount: number };
        };
      }) => response.data,
      invalidatesTags: ['Finance', 'Workspace']
    }),

    // Список дебиторской задолженности
    getReceivablesList: builder.query<ReceivablesListResponse, {
      page?: number;
      limit?: number;
      applicationId?: number;
      routeId?: number;
      status?: string;
      search?: string;
    }>({
      query: (params) => ({
        url: '/finance/receivables',
        method: 'GET',
        params
      }),
      transformResponse: (response: { success: boolean; data: ReceivablesListResponse }) =>
        response?.data ?? { receivables: [], pagination: { page: 1, limit: 20, total: 0 } },
      providesTags: ['Finance']
    }),

    // Список кредиторской задолженности
    getPayablesList: builder.query<PayablesListResponse, {
      page?: number;
      limit?: number;
      routeId?: number;
      status?: string;
      search?: string;
    }>({
      query: (params) => ({
        url: '/finance/payables',
        method: 'GET',
        params
      }),
      transformResponse: (response: { success: boolean; data: PayablesListResponse }) =>
        response?.data ?? { payables: [], pagination: { page: 1, limit: 20, total: 0 } },
      providesTags: ['Finance']
    }),

    // Финансовый дашборд (KPI)
    getFinanceDashboard: builder.query<FinanceDashboardSummary, void>({
      query: () => ({
        url: '/finance/dashboard',
        method: 'GET'
      }),
      transformResponse: (response: { success: boolean; data: FinanceDashboardSummary }) =>
        response?.data,
      providesTags: ['Finance']
    }),

    // Финансовая сводка маршрута
    getRouteFinanceSummary: builder.query<RouteFinanceSummary, number>({
      query: (routeId) => ({ url: `/finance/routes/${routeId}/summary`, method: 'GET' }),
      transformResponse: (response: { success: boolean; data: RouteFinanceSummary }) => response?.data,
      providesTags: (result, error, routeId) => [{ type: 'RouteExpenses', id: routeId }, 'Finance']
    }),

    // Расходы по маршруту (RouteExpense)
    getRouteExpenses: builder.query<
      RouteExpensesQueryResult,
      { routeId: number; includeDraft?: boolean; includeRejected?: boolean }
    >({
      query: ({ routeId, includeDraft, includeRejected }) => ({
        url: `/finance/routes/${routeId}/expenses`,
        params: {
          ...(includeDraft ? { includeDraft: 'true' } : {}),
          ...(includeRejected ? { includeRejected: 'true' } : {})
        }
      }),
      transformResponse: (response: {
        success: boolean;
        data: RouteExpense[];
        allocationCandidates?: RouteExpenseAllocationCandidate[];
      }) => ({
        expenses: response?.data ?? [],
        allocationCandidates: response?.allocationCandidates ?? []
      }),
      providesTags: (result, error, { routeId }) => [{ type: 'RouteExpenses', id: routeId }, 'RouteExpenses']
    }),

    createRouteExpense: builder.mutation<RouteExpense, { routeId: number; data: CreateRouteExpenseRequest }>({
      query: ({ routeId, data }) => ({
        url: `/finance/routes/${routeId}/expenses`,
        method: 'POST',
        body: data
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: (result, error, { routeId }) => [{ type: 'RouteExpenses', id: routeId }, 'Route', 'Finance']
    }),
    updateRouteExpense: builder.mutation<RouteExpense, { routeId: number; expenseId: number; data: UpdateRouteExpenseRequest }>({
      query: ({ routeId, expenseId, data }) => ({
        url: `/finance/routes/${routeId}/expenses/${expenseId}`,
        method: 'PATCH',
        body: data
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: (result, error, { routeId }) => [{ type: 'RouteExpenses', id: routeId }, 'Route', 'Finance']
    }),
    deleteRouteExpense: builder.mutation<{ id: number }, { routeId: number; expenseId: number }>({
      query: ({ routeId, expenseId }) => ({
        url: `/finance/routes/${routeId}/expenses/${expenseId}`,
        method: 'DELETE'
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (result, error, { routeId }) => [{ type: 'RouteExpenses', id: routeId }, 'Route', 'Finance']
    }),
    updateRouteExpenseStatus: builder.mutation<RouteExpense, { routeId: number; expenseId: number; status: 'DRAFT' | 'CONFIRMED' | 'REJECTED' }>({
      query: ({ routeId, expenseId, status }) => ({
        url: `/finance/routes/${routeId}/expenses/${expenseId}/status`,
        method: 'PATCH',
        body: { status }
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: (result, error, { routeId }) => [{ type: 'RouteExpenses', id: routeId }, 'Route', 'Finance']
    }),
    setRouteExpenseAllocation: builder.mutation<
      { success: boolean },
      { routeId: number; expenseId: number; data: SetRouteExpenseAllocationRequest }
    >({
      query: ({ routeId, expenseId, data }) => ({
        url: `/finance/routes/${routeId}/expenses/${expenseId}/allocation`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (result, error, { routeId }) => [
        { type: 'RouteExpenses', id: routeId },
        'Route',
        'Finance',
        'ApplicationFinance'
      ]
    }),

    // Создать новый платеж
    createPayment: builder.mutation<CreatePaymentResult, CreatePaymentRequest>({
      query: (body) => ({
        url: '/finance/payments',
        method: 'POST',
        body
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: ['Payments', 'Finance', 'Route', 'Workspace'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* empty */
        }
      },
    }),

    // Обновить платеж
    updatePayment: builder.mutation<Payment, {
      id: number;
      data: UpdatePaymentRequest;
    }>({
      query: ({ id, data }) => ({
        url: `/finance/payments/${id}`,
        method: 'PATCH',
        body: data
      }),
      transformResponse: (response: any) => response?.data,
      invalidatesTags: ['Payments', 'Finance'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* empty */
        }
      },
    }),

    // Удалить платеж
    deletePayment: builder.mutation<{ success: boolean } | any, number>({
      query: (id) => ({
        url: `/finance/payments/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ['Payments', 'Finance']
    }),

    // === Новые эндпоинты: документы финансового модуля ===

    // Загрузка документов погрузки
    uploadLoadingFinanceDocs: builder.mutation<LoadingDocumentDTO, {
      routeId: number;
      loadingDate: string;
      photos: string[];
      invoiceDocument?: string | null;
    }>({
      query: (body) => ({
        url: '/finance/loading-documents',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Finance']
    }),

    // Загрузка транспортных документов
    uploadTransportFinanceDocs: builder.mutation<TransportDocumentDTO, {
      routeId: number;
      deliveryType: 'MAIL' | 'EDO' | 'PERSONAL';
      trackingNumber?: string;
      receivedDate?: string;
      documents: string[];
      originals?: string[]; // новые: оригинальные ТН
    }>({
      query: (body) => ({
        url: '/finance/transport-documents',
        method: 'POST',
        body
      }),
      invalidatesTags: ['Finance']
    }),

    // Загрузка первичных документов заявки
    uploadApplicationPrimaryDocs: builder.mutation<{ success: boolean; data: any }, { applicationId: number; files: string[] }>({
      query: ({ applicationId, files }) => ({
        url: `/finance/applications/${applicationId}/primary-docs`,
        method: 'POST',
        body: { files }
      }),
      invalidatesTags: ['Finance']
    }),

    // Списки документов по маршруту
    getLoadingFinanceDocs: builder.query<LoadingDocumentDTO[], number>({
      query: (routeId) => `/finance/routes/${routeId}/loading-documents`,
      transformResponse: (response: { success: boolean; data: LoadingDocumentDTO[] }) => response.data,
      providesTags: ['Finance']
    }),
    getTransportFinanceDocs: builder.query<TransportDocumentDTO[], number>({
      query: (routeId) => `/finance/routes/${routeId}/transport-documents`,
      transformResponse: (response: { success: boolean; data: TransportDocumentDTO[] }) => response.data,
      providesTags: ['Finance']
    }),

    // Обновить доход маршрута
    updateRouteIncome: builder.mutation<{ success: boolean; data: any }, { routeId: number; income: number }>({
      query: ({ routeId, income }) => ({
        url: `/finance/routes/${routeId}/income`,
        method: 'PATCH',
        body: { income }
      }),
      invalidatesTags: ['Finance']
    }),
    // Получить список фактических поступлений от партнёра по заявке
    getPartnerPayments: builder.query<any[], number>({
      query: (applicationId) => `/finance/applications/${applicationId}/partner-payments`,
      transformResponse: (response: { success: boolean; data: any[] }) => response.data,
      providesTags: (result, error, appId) => [{ type: 'Finance' }]
    }),

    // Создать поступление от партнёра
    createPartnerPayment: builder.mutation<any, { applicationId: number; amount: number; paymentDate?: string; note?: string }>({
      query: ({ applicationId, ...body }) => ({
        url: `/finance/applications/${applicationId}/partner-payments`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        'Finance',
        { type: 'ApplicationFinance', id: applicationId }
      ]
    }),
  })
});

export const {
  useGetApplicationsFinanceListQuery,
  useGetApplicationFinanceQuery,
  useUpdatePrimaryDocsStatusMutation,
  useUpdatePartnerPaymentStatusMutation,
  useSetPartnerReceivableMutation,
  useGetPartnerPaymentsQuery,
  useCreatePartnerPaymentMutation,
  useGetRoutesReadModelQuery,
  useGetRoutesExtendedQuery,
  useGetApplicationsExtendedQuery,
  useGetObligationsReadQuery,
  useGetLogistsControlSummaryQuery,
  useGetLogistRoutesWithIssuesQuery,
  useGetCarriersControlSummaryQuery,
  useGetCarrierRoutesWithIssuesQuery,
  useGetClientsControlSummaryQuery,
  useGetClientRoutesWithIssuesQuery,
  useGetRoutesFinanceListQuery,
  useGetFinanceStatisticsQuery,
  useGetAvailableRoutesForPaymentsQuery,
  useGetRoutePaymentCountersQuery,
  useGetRouteObligationsForPaymentQuery,
  useGetPaymentsListQuery,
  useGetInvoicesListQuery,
  useSendInvoiceToClientMutation,
  useUpdateInvoiceMutation,
  useCancelInvoiceMutation,
  useAdjustClientInvoiceMutation,
  useGetReceivablesListQuery,
  useGetPayablesListQuery,
  useGetFinanceDashboardQuery,
  useGetRouteFinanceSummaryQuery,
  useGetRouteExpensesQuery,
  useCreateRouteExpenseMutation,
  useUpdateRouteExpenseMutation,
  useDeleteRouteExpenseMutation,
  useUpdateRouteExpenseStatusMutation,
  useSetRouteExpenseAllocationMutation,
  useCreatePaymentMutation,
  useUpdatePaymentMutation,
  useDeletePaymentMutation,
  useUploadLoadingFinanceDocsMutation,
  useUploadTransportFinanceDocsMutation,
  useUploadApplicationPrimaryDocsMutation,
  useGetLoadingFinanceDocsQuery,
  useGetTransportFinanceDocsQuery,
  useUpdateRouteIncomeMutation,
} = financeApi; 